/**
 * @file 게시판 유형 생성·수정 다이얼로그 — <b>전체 관리자 전용</b>.
 *
 * 사이드탭의 '추가' / 각 게시판의 톱니바퀴에서 열린다. 서버도 같은 권한을 다시 검사하므로
 * 이 화면이 열렸다는 것만으로 권한이 생기지는 않는다.
 */
import React, { useEffect, useState } from "react";
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    Stack, Switch, TextField, Typography,
} from "@mui/material";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ActionButton from "../../../component/common/ActionButton";
import FormField from "../../../component/common/FormField";
import { boardApi } from "../api/boardApi";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import { notify } from "../../../util/toast";
import { runApiAction } from "../../../util/apiFeedback";
import { C } from "../boardShared";

const EMPTY = { name: "", description: "", anonymous: false, writeAdminOnly: false, active: true };

export default function BoardTypeDialog({ open, onClose, boardType, onSaved, onDeleted }) {
    const editing = Boolean(boardType);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(boardType ? {
            name: boardType.name || "",
            description: boardType.description || "",
            anonymous: Boolean(boardType.anonymous),
            writeAdminOnly: Boolean(boardType.writeAdminOnly),
            active: boardType.active !== false,
        } : EMPTY);
    }, [open, boardType]);

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const handleSave = () => {
        if (!form.name.trim()) { notify("게시판명을 입력하세요", "error"); return; }
        return runApiAction({
            request: () => (editing
                ? boardApi.updateType(boardType.id, form)
                : boardApi.createType(form)),
            successMsg: editing ? "게시판이 수정되었습니다" : "게시판이 생성되었습니다",
            errorMsg: editing ? "게시판 수정 실패" : "게시판 생성 실패",
            setLoading: setSaving,
            onSuccess: onSaved,
            closeOnSuccess: onClose,
        });
    };

    const handleDelete = async () => {
        if (!(await confirmDialog({
            message: `'${boardType.name}' 게시판을 삭제하시겠습니까?\n글이 남아 있으면 삭제되지 않습니다.`,
        }))) return;
        return runApiAction({
            request: () => boardApi.deleteType(boardType.id),
            successMsg: "게시판이 삭제되었습니다",
            errorMsg: "게시판 삭제 실패",
            setLoading: setDeleting,
            onSuccess: onDeleted,
            closeOnSuccess: onClose,
        });
    };

    /** 익명 게시판을 실명으로 되돌리는 것은 서버가 거부한다 — 과거 글의 작성자가 드러나기 때문. */
    const anonymousLocked = editing && Boolean(boardType?.anonymous);

    return (
        <Dialog open={open} onClose={saving || deleting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>
                {editing ? "게시판 설정" : "새 게시판 만들기"}
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Stack spacing={2}>
                    <FormField label="게시판명" required>
                        <TextField size="small" fullWidth
                            value={form.name}
                            onChange={e => set("name", e.target.value.slice(0, 100))}
                            placeholder="예: 스터디 게시판" />
                    </FormField>

                    <FormField label="설명">
                        <TextField size="small" fullWidth multiline rows={2}
                            value={form.description}
                            onChange={e => set("description", e.target.value.slice(0, 500))}
                            placeholder="목록 상단에 표시되는 안내문" />
                    </FormField>

                    <Box>
                        <FormControlLabel
                            control={
                                <Switch size="small" checked={form.anonymous} disabled={anonymousLocked}
                                    onChange={e => set("anonymous", e.target.checked)} />
                            }
                            label={
                                <Typography sx={{ fontSize: 13 }}>
                                    익명 게시판
                                    <Box component="span" sx={{ color: C.textMuted, ml: 0.75 }}>
                                        글·댓글 작성자를 표시하지 않습니다
                                    </Box>
                                </Typography>
                            }
                        />
                        {anonymousLocked && (
                            <Typography sx={{ fontSize: 11.5, color: C.warning, ml: 5.5 }}>
                                한 번 켠 익명 게시판은 실명으로 되돌릴 수 없습니다 (과거 글의 작성자가 드러납니다).
                            </Typography>
                        )}
                    </Box>

                    <FormControlLabel
                        control={
                            <Switch size="small" checked={form.writeAdminOnly}
                                onChange={e => set("writeAdminOnly", e.target.checked)} />
                        }
                        label={
                            <Typography sx={{ fontSize: 13 }}>
                                글쓰기 관리자 전용
                                <Box component="span" sx={{ color: C.textMuted, ml: 0.75 }}>
                                    읽기·댓글은 전 직원 그대로
                                </Box>
                            </Typography>
                        }
                    />

                    <FormControlLabel
                        control={
                            <Switch size="small" checked={form.active}
                                onChange={e => set("active", e.target.checked)} />
                        }
                        label={
                            <Typography sx={{ fontSize: 13 }}>
                                사용
                                <Box component="span" sx={{ color: C.textMuted, ml: 0.75 }}>
                                    끄면 목록에서 숨겨지고 새 글을 쓸 수 없습니다 (기존 글은 보존)
                                </Box>
                            </Typography>
                        }
                    />

                    {editing && boardType.systemDefault && (
                        <Typography sx={{ fontSize: 12, color: C.textMuted }}>
                            * 기본 게시판은 삭제할 수 없습니다. 운영에서 빼려면 '사용' 을 꺼 주세요.
                        </Typography>
                    )}

                    {editing && !boardType.systemDefault && (
                        <Box sx={{ pt: 1.5, borderTop: `1px solid ${C.errorBg}` }}>
                            <ActionButton variant="delete"
                                startIcon={<DeleteRoundedIcon sx={{ fontSize: 14 }} />}
                                onClick={handleDelete} loading={deleting}
                                sx={{ width: "100%", fontSize: 12 }}
                                label="게시판 삭제 (글이 없을 때만)" />
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <ActionButton variant="cancel" onClick={onClose} disabled={saving || deleting} />
                <ActionButton variant="save" onClick={handleSave} loading={saving} />
            </DialogActions>
        </Dialog>
    );
}
